import React from "react";

// Base UI
import baseuiTheme from "../../../../styling/baseui_theme";
import {
  FormControl as BaseFormControl,
  FormControlProps as BaseFormControlProps,
} from "baseui/form-control";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";

// Styletron
import { styled } from "styletron-react";

// Carbon icons
import { ErrorFilled, InformationFilled } from "@carbon/icons-react";
import { Tooltip } from "../../surfaces/tooltip";

export enum SIZE {
  mini = "mini",
  default = "default",
  compact = "compact",
  large = "large",
}

export interface FormControlProps extends BaseFormControlProps {
  size?: SIZE;
  error?: boolean | React.ReactNode;
  dataTest?: string;
  tooltipText?: string;
  errorDataAnalyticsId?: string;
}

const getFormControlOverrides = (props: FormControlProps): Overrides<BaseFormControl> => {
  const { size } = props;
  const labelSize =
    size === SIZE.mini || size === SIZE.compact
      ? baseuiTheme.typography.ParagraphSmall.fontSize
      : SIZE.large === size
        ? baseuiTheme.typography.ParagraphLarge.fontSize
        : baseuiTheme.typography.ParagraphMedium.fontSize;

  const overrideDefaultColor =
    typeof props.positive === "undefined" && typeof props.error === "undefined";
  const captionColorOverride = props.disabled
    ? { color: baseuiTheme.colors.mono400 }
    : overrideDefaultColor
      ? {
          color: baseuiTheme.colors.contentTertiary,
        }
      : {};

  const labelColorOverride = props.disabled ? { color: baseuiTheme.colors.mono400 } : {};

  return {
    Label: {
      style: () => {
        return {
          fontSize: labelSize,
          marginBottom: "0.75em",
          ...labelColorOverride,
        };
      },
    },
    Caption: {
      style: () => {
        return {
          fontSize: baseuiTheme.typography.ParagraphSmall.fontSize,
          ...captionColorOverride,
        };
      },
    },
  };
};

const StyledError = styled("div", {
  marginTop: "1em",
  display: "flex",
  gap: "0.5em",
  flexGrow: 1,
  fontSize: baseuiTheme.typography.LabelSmall.fontSize,
});

/*
  The defined height is for being able to align the icon with the first line of the paragraph. However, we can't:
  - Only use the 'align-items' property because the ErrorIconContainer will, automatically, take 100% of the height, and therefor
    align itself with the center of the whole error content.
  - Set the 'align-items' to top, or skip this property for that matter, because the ErrorFilled16 element is an SVG, and not a
    text element, so it will not leave the "top margin" defined by 'line-height' that otherwise text paragraphs would into consideration.
*/
const StyledErrorIconContent = styled("span", {
  display: "flex",
  alignItems: "center",
  height: "1.5em",
});

const StyledTooltipContainer = styled("span", {
  display: "inline-flex",
  gap: "4px",
  alignItems: "center",
});

export const FormControl = ({
  overrides,
  dataTest,
  errorDataAnalyticsId,
  ...props
}: FormControlProps) => {
  const errorDataTest = dataTest ? `${dataTest}-error` : "";

  const ErrorContent = (
    <StyledError data-test={errorDataTest} aria-live="assertive">
      <StyledErrorIconContent>
        <ErrorFilled size={16} />
      </StyledErrorIconContent>
      <span data-analytics-id={errorDataAnalyticsId}>
        {/* base ui types aren't playing well with react types */}
        {/* eslint-disable-next-line */}
        {/* @ts-ignore-next-line */}
        {props.error && typeof props.error === "boolean"
          ? props.caption
          : props.error && typeof props.error !== "boolean" // Here, we can't use the 'instanceof' operator to know if its a ReactNode because React.ReactNode is a type, not a class
            ? props.error
            : null}
      </span>
    </StyledError>
  );

  const label = props.tooltipText ? (
    <StyledTooltipContainer>
      {/* eslint-disable-next-line */}
      {/* @ts-ignore */}
      <>
        {props.label}{" "}
        <Tooltip content={props.tooltipText}>
          <InformationFilled size={20} className="info-tooltip" />
        </Tooltip>
      </>
    </StyledTooltipContainer>
  ) : (
    props.label
  );

  return (
    <BaseFormControl
      // This has to be done to overcome an a11y bug in Base.
      // If you pass a value for error that is not a boolean, the 'aria-invalid' property from the child
      // will have an invalid content.
      {...(props.error
        ? // in the case of a checkbox group, we need each element in the form group
          // to change color, but it is not useful to show the error message on every checkbox in the group
          { ...props, error: true, caption: props.error === "no-caption" ? null : ErrorContent }
        : { ...props, error: false })}
      overrides={mergeOverrides(
        getFormControlOverrides(props),
        overrides as Overrides<BaseFormControl>,
      )}
      label={label}
      data-test={dataTest}
    />
  );
};

export default FormControl;
